/**
 * ElecBid — CEC 2024 Residential Load Calculator
 * Browning Electric
 *
 * CEC 2024 (26th Edition) — Rule 8-200 Basic Demand Calculation
 * for Residential Services.
 *
 * Structured to EXACTLY follow CEC Rule 8-200(1)(a) in code-book order.
 *
 * Props:
 *   embedded       — (bool) true when inside an assessment form (compact mode)
 *   presetNewLoad  — (object) { label, watts } auto-populated from parent assessment
 *   onResult       — (fn) callback with { demandWatts, demandAmps, serviceAmps, headroom, overCapacity }
 *   defaultServiceSize — (string) pre-select service size e.g. "200"
 *   initialState   — (object) seed all input fields (for reloading a saved calc).
 *                     Apply once on mount — to reload, change `key` on the parent.
 *   onStateChange  — (fn) callback with { inputs, result, serviceAmps } whenever
 *                     any input or computed result changes. Used by the
 *                     LoadCalcWorkspace save/PDF flow.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import {
  calculateCECDemand, normalizeClimateItems, generatorScenario, additionalDemandFor,
  CLIMATE_TYPES, GEN_MODES, climateTypeLabel, climateTypeSide,
} from "../lib/cecLoadCalc";
import { FUELS } from "../lib/generatorSpecs";

// ─── Constants ──────────────────────────────────────────────────────────────────
// SQ_FT_TO_SQ_M and VOLTAGE moved to src/lib/cecLoadCalc.js with the engine.
const SERVICE_SIZES = ["100", "125", "150", "200"];

const EV_PRESETS = [
  { label: "50A (12,000W)", watts: 12000 },
  { label: "60A (14,400W)", watts: 14400 },
  { label: "100A (24,000W)", watts: 24000 },
];

const ADDITIONAL_LOAD_PRESETS = [
  { label: "Storage Water Heater", defaultW: 3000 },
  { label: "Clothes Dryer", defaultW: 5500 },
  { label: "Dishwasher", defaultW: 1800 },
  { label: "Pool Pump", defaultW: 1500 },
  { label: "Sauna", defaultW: 6000 },
];

const NEW_LOAD_PRESETS = [
  { label: "EV Charger 50A", watts: 12000 },
  { label: "EV Charger 60A", watts: 14400 },
  { label: "Hot Tub", watts: 6000 },
  { label: "Sauna", watts: 6000 },
  { label: "Pool Heater", watts: 5000 },
  { label: "Generator Interlock", watts: 0 },
];

// The CEC Rule 8-200 engine used to live here. It now lives in
// src/lib/cecLoadCalc.js so the calculator, the saved-calc API and the AI
// tools in the assessment / quote chats all answer from ONE copy of the
// arithmetic — a second copy is how a quote and a permit drawing end up
// disagreeing. Verified byte-identical against all 15 saved calcs before the
// move (scripts/test_cec_load_calc.js pins two of them).

// ─── Styles ────────────────────────────────────────────────────────────────────
const CSS = `
  .lc-wrap {
    font-family: 'IBM Plex Sans', system-ui, sans-serif;
    color: #e8eaf0;
  }
  .lc-wrap.lc-standalone {
    background: #0d0f12;
    padding: 0;
  }

  /* Section card */
  .lc-card {
    background: #161a20;
    border: 1px solid #2a3140;
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 12px;
  }
  .lc-card-title {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #8892a4;
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .lc-rule-ref {
    font-size: 10px;
    font-weight: 600;
    color: #a5ce3a;
    background: rgba(165,206,58,0.1);
    border: 1px solid rgba(165,206,58,0.2);
    border-radius: 4px;
    padding: 2px 6px;
    font-family: 'IBM Plex Mono', monospace;
    white-space: nowrap;
  }

  /* Input field */
  .lc-input-row {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
  }
  .lc-input-label {
    font-size: 13px;
    font-weight: 500;
    color: #c4c9d4;
    min-width: 90px;
  }
  .lc-input {
    flex: 1;
    max-width: 140px;
    background: #1e2430;
    border: 1.5px solid #2a3140;
    border-radius: 8px;
    color: #e8eaf0;
    font-size: 15px;
    font-weight: 600;
    padding: 10px 12px;
    font-family: 'IBM Plex Mono', monospace;
    outline: none;
    transition: border-color 0.15s;
  }
  .lc-input:focus {
    border-color: #a5ce3a;
  }
  .lc-input-unit {
    font-size: 12px;
    color: #8892a4;
    font-weight: 500;
  }
  .lc-input-hint {
    font-size: 11px;
    color: #6b7589;
    margin-top: 2px;
    line-height: 1.4;
  }
  .lc-calc-preview {
    font-size: 11px;
    color: #a5ce3a;
    font-family: 'IBM Plex Mono', monospace;
    margin-top: 4px;
    padding: 4px 8px;
    background: rgba(165,206,58,0.06);
    border-radius: 4px;
    display: inline-block;
  }

  /* Service size pills */
  .lc-service-row {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
  .lc-service-pill {
    padding: 8px 16px;
    border-radius: 8px;
    border: 1.5px solid #2a3140;
    background: #1e2430;
    color: #8892a4;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s;
    font-family: 'IBM Plex Mono', monospace;
  }
  .lc-service-pill:hover {
    border-color: #374152;
  }
  .lc-service-pill.active {
    border-color: #a5ce3a;
    background: rgba(165,206,58,0.12);
    color: #a5ce3a;
  }

  /* Toggle switch */
  .lc-toggle-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 0;
    border-bottom: 1px solid #1e2430;
  }
  .lc-toggle-row:last-child { border-bottom: none; }

  .lc-toggle {
    width: 40px;
    height: 24px;
    border-radius: 12px;
    border: none;
    cursor: pointer;
    position: relative;
    transition: background 0.2s;
    flex-shrink: 0;
  }
  .lc-toggle.off { background: #2a3140; }
  .lc-toggle.on { background: #a5ce3a; }
  .lc-toggle-knob {
    position: absolute;
    top: 3px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #fff;
    transition: left 0.2s;
    box-shadow: 0 1px 3px rgba(0,0,0,0.3);
  }
  .lc-toggle.off .lc-toggle-knob { left: 3px; }
  .lc-toggle.on .lc-toggle-knob { left: 19px; }

  .lc-toggle-info { flex: 1; min-width: 0; }
  .lc-toggle-label {
    font-size: 13px;
    font-weight: 600;
    color: #e8eaf0;
  }
  .lc-toggle-label.disabled { color: #555f72; }
  .lc-toggle-sub {
    font-size: 11px;
    color: #6b7589;
    margin-top: 2px;
  }
  .lc-demand-badge {
    font-size: 11px;
    font-family: 'IBM Plex Mono', monospace;
    color: #a5ce3a;
    background: rgba(165,206,58,0.08);
    padding: 2px 8px;
    border-radius: 4px;
    white-space: nowrap;
  }

  /* EMS / load-shed toggle on individual loads. When "on" the load is
     controlled by an energy management system and fully shed from the calc. */
  .lc-shed-btn {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.04em;
    padding: 3px 7px;
    border-radius: 5px;
    border: 1px solid #2a3140;
    background: #1e2430;
    color: #8892a4;
    cursor: pointer;
    white-space: nowrap;
    flex-shrink: 0;
    transition: all 0.15s;
  }
  .lc-shed-btn:hover { border-color: #f59e0b; color: #f59e0b; }
  .lc-shed-btn.on {
    border-color: #f59e0b;
    background: rgba(245,158,11,0.14);
    color: #f59e0b;
  }
  .lc-shed-note {
    font-size: 10px;
    color: #f59e0b;
    font-weight: 600;
  }

  .lc-watts-input {
    width: 90px;
    background: #1e2430;
    border: 1px solid #2a3140;
    border-radius: 6px;
    color: #e8eaf0;
    font-size: 13px;
    font-weight: 600;
    padding: 6px 8px;
    font-family: 'IBM Plex Mono', monospace;
    outline: none;
    text-align: right;
    flex-shrink: 0;
  }
  .lc-watts-input:focus { border-color: #a5ce3a; }
  .lc-watts-unit {
    font-size: 11px;
    color: #555f72;
    flex-shrink: 0;
  }

  /* Sub-items inside a section */
  .lc-sub-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 0 8px 12px;
    border-bottom: 1px solid rgba(42,49,64,0.5);
  }
  .lc-sub-item:last-child { border-bottom: none; }

  /* EV preset pills */
  .lc-ev-pills {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    margin-bottom: 10px;
  }
  .lc-ev-pill {
    padding: 6px 12px;
    border-radius: 6px;
    border: 1.5px solid #2a3140;
    background: #1e2430;
    color: #8892a4;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s;
  }
  .lc-ev-pill.active {
    border-color: #a5ce3a;
    background: rgba(165,206,58,0.12);
    color: #a5ce3a;
  }

  /* Additional loads list */
  .lc-add-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 0;
    border-bottom: 1px solid rgba(42,49,64,0.5);
  }
  .lc-add-item:last-child { border-bottom: none; }
  .lc-add-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border-radius: 8px;
    border: 1.5px dashed #2a3140;
    background: transparent;
    color: #8892a4;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s;
    margin-top: 8px;
  }
  .lc-add-btn:hover {
    border-color: #a5ce3a;
    color: #a5ce3a;
  }
  .lc-remove-btn {
    background: none;
    border: none;
    color: #555f72;
    cursor: pointer;
    font-size: 16px;
    padding: 2px 6px;
    border-radius: 4px;
    transition: color 0.15s;
    flex-shrink: 0;
  }
  .lc-remove-btn:hover { color: #ef4444; }

  .lc-add-dropdown {
    position: relative;
    display: inline-block;
  }
  .lc-add-menu {
    position: absolute;
    bottom: calc(100% + 4px);
    left: 0;
    background: #1e2430;
    border: 1px solid #2a3140;
    border-radius: 8px;
    padding: 4px;
    min-width: 180px;
    z-index: 10;
    box-shadow: 0 8px 24px rgba(0,0,0,0.4);
  }
  .lc-add-menu-item {
    display: block;
    width: 100%;
    padding: 8px 12px;
    background: none;
    border: none;
    color: #c4c9d4;
    font-size: 12px;
    font-weight: 500;
    text-align: left;
    cursor: pointer;
    border-radius: 6px;
    transition: background 0.1s;
  }
  .lc-add-menu-item:hover {
    background: rgba(165,206,58,0.1);
    color: #a5ce3a;
  }

  /* New load section */
  .lc-new-load {
    background: rgba(165,206,58,0.04);
    border: 1.5px solid rgba(165,206,58,0.15);
    border-radius: 10px;
    padding: 14px;
  }
  .lc-new-load-title {
    font-size: 12px;
    font-weight: 700;
    color: #a5ce3a;
    margin-bottom: 10px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
  .lc-nl-pills {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    margin-bottom: 10px;
  }
  .lc-nl-pill {
    padding: 6px 12px;
    border-radius: 6px;
    border: 1.5px solid #2a3140;
    background: #1e2430;
    color: #8892a4;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s;
  }
  .lc-nl-pill.active {
    border-color: #a5ce3a;
    background: rgba(165,206,58,0.15);
    color: #a5ce3a;
  }
  .lc-nl-custom {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
  }

  /* Note / info box */
  .lc-note {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 10px 12px;
    background: rgba(165,206,58,0.04);
    border: 1px solid rgba(165,206,58,0.12);
    border-radius: 8px;
    margin-top: 10px;
    font-size: 11px;
    color: #8892a4;
    line-height: 1.5;
  }
  .lc-note.warn {
    background: rgba(245,158,11,0.06);
    border-color: rgba(245,158,11,0.2);
  }
  .lc-note-icon {
    flex-shrink: 0;
    color: #a5ce3a;
    font-size: 13px;
    margin-top: 1px;
  }
  .lc-note.warn .lc-note-icon { color: #fbbf24; }

  /* Result panel */
  .lc-result {
    background: #161a20;
    border: 1.5px solid #2a3140;
    border-radius: 12px;
    padding: 16px;
    margin-top: 12px;
  }
  .lc-result.green { border-color: rgba(16,185,129,0.4); }
  .lc-result.amber { border-color: rgba(245,158,11,0.4); }
  .lc-result.red   { border-color: rgba(239,68,68,0.4); }

  .lc-result-title {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #8892a4;
    margin-bottom: 12px;
  }

  /* Capacity bar */
  .lc-capacity-bar-wrap { margin-bottom: 14px; }
  .lc-capacity-labels {
    display: flex;
    justify-content: space-between;
    font-size: 11px;
    color: #8892a4;
    margin-bottom: 6px;
  }
  .lc-capacity-bar {
    height: 12px;
    background: #1e2430;
    border-radius: 6px;
    overflow: hidden;
    position: relative;
  }
  .lc-capacity-fill {
    height: 100%;
    border-radius: 6px;
    transition: width 0.4s ease, background 0.3s;
  }
  .lc-capacity-fill.green { background: linear-gradient(90deg, #10b981, #34d399); }
  .lc-capacity-fill.amber { background: linear-gradient(90deg, #f59e0b, #fbbf24); }
  .lc-capacity-fill.red   { background: linear-gradient(90deg, #ef4444, #f87171); }

  /* Summary line */
  .lc-summary {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px;
    border-radius: 8px;
    margin-bottom: 10px;
  }
  .lc-summary.green {
    background: rgba(16,185,129,0.08);
    border: 1px solid rgba(16,185,129,0.2);
  }
  .lc-summary.amber {
    background: rgba(245,158,11,0.08);
    border: 1px solid rgba(245,158,11,0.2);
  }
  .lc-summary.red {
    background: rgba(239,68,68,0.08);
    border: 1px solid rgba(239,68,68,0.2);
  }
  .lc-summary-icon { font-size: 20px; flex-shrink: 0; }
  .lc-summary-text {
    font-size: 13px;
    font-weight: 600;
    line-height: 1.4;
  }
  .lc-summary.green .lc-summary-text { color: #34d399; }
  .lc-summary.amber .lc-summary-text { color: #fbbf24; }
  .lc-summary.red   .lc-summary-text { color: #f87171; }

  /* Breakdown rows */
  .lc-breakdown {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid #2a3140;
  }
  .lc-br-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 0;
    font-size: 12px;
  }
  .lc-br-label { color: #8892a4; }
  .lc-br-value {
    font-family: 'IBM Plex Mono', monospace;
    font-weight: 600;
    color: #c4c9d4;
  }
  .lc-br-row.total {
    margin-top: 6px;
    padding-top: 8px;
    border-top: 1px solid #2a3140;
  }
  .lc-br-row.total .lc-br-label,
  .lc-br-row.total .lc-br-value {
    font-size: 13px;
    font-weight: 700;
    color: #e8eaf0;
  }
  .lc-br-sub {
    padding-left: 12px;
    font-size: 11px;
  }
  .lc-br-sub .lc-br-label { color: #6b7589; font-style: italic; }
  .lc-br-sub .lc-br-value { color: #8892a4; font-weight: 500; }

  .lc-section-total {
    font-size: 11px;
    color: #a5ce3a;
    font-family: 'IBM Plex Mono', monospace;
    text-align: right;
    padding: 6px 0 2px;
    border-top: 1px solid rgba(165,206,58,0.1);
    margin-top: 4px;
  }

  .lc-footer {
    text-align: center;
    font-size: 10px;
    color: #555f72;
    margin-top: 16px;
    padding-top: 12px;
    border-top: 1px solid #1e2430;
  }

  /* Embedded mode */
  .lc-wrap.lc-embedded .lc-card {
    background: #1e2430;
    border-color: #374152;
  }
  .lc-wrap.lc-embedded .lc-result {
    background: #1e2430;
  }

  /* EVEMS checkbox */
  .lc-checkbox-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
    font-size: 12px;
    color: #8892a4;
  }
  .lc-checkbox {
    accent-color: #a5ce3a;
  }

  /* Name input for custom items */
  .lc-name-input {
    background: #1e2430;
    border: 1px solid #2a3140;
    border-radius: 6px;
    color: #e8eaf0;
    font-size: 12px;
    font-weight: 500;
    padding: 6px 8px;
    font-family: 'IBM Plex Sans', sans-serif;
    outline: none;
    min-width: 100px;
    flex: 1;
  }
  .lc-name-input:focus { border-color: #a5ce3a; }
`;

// ─── Toggle Component ──────────────────────────────────────────────────────────
function Toggle({ on, onToggle, label, subLabel, disabled: disabledLabel, children }) {
  return (
    <div className="lc-toggle-row">
      <button
        type="button"
        className={`lc-toggle ${on ? "on" : "off"}`}
        onClick={onToggle}
        aria-label={`Toggle ${label}`}
      >
        <span className="lc-toggle-knob" />
      </button>
      <div className="lc-toggle-info">
        <div className={`lc-toggle-label ${!on && disabledLabel !== false ? "disabled" : ""}`}>
          {label}
        </div>
        {subLabel && <div className="lc-toggle-sub">{subLabel}</div>}
      </div>
      {children}
    </div>
  );
}

// ─── Component ─────────────────────────────────────────────────────────────────
export default function CECLoadCalc({
  embedded = false,
  presetNewLoad = null,
  onResult = null,
  defaultServiceSize = "200",
  initialState = null,
  onStateChange = null,
}) {
  // initialState seeds defaults for every input when reloading a saved calc.
  // Falls back to the original hardcoded defaults when no field is provided.
  const seed = initialState || {};

  // ── Section 1: Living Area
  const [aboveGradeSqFt, setAboveGradeSqFt] = useState(seed.aboveGradeSqFt ?? "");
  const [basementSqFt, setBasementSqFt] = useState(seed.basementSqFt ?? "");

  // ── Section 2: Service Size
  const [serviceSize, setServiceSize] = useState(seed.serviceSize || defaultServiceSize);

  // ── Section 3a/3b: Climate loads (heating + A/C)
  // One labelled row per appliance instead of two bulked scalars. A reloaded
  // legacy calc migrates its heatingWatts/acWatts into rows on the way in
  // (normalizeClimateItems), so an old saved calc reopens with the same
  // numbers and can then be split apart.
  const [climateItems, setClimateItems] = useState(() => normalizeClimateItems(seed));
  // Whether heating and AC can run at the same time. Default false matches
  // the prior "always use the larger" behaviour; user must opt in when both
  // systems are independent (e.g. baseboard + central AC).
  const [heatAcSimultaneous, setHeatAcSimultaneous] = useState(seed.heatAcSimultaneous ?? false);

  const updateClimateItem = (id, field, value) =>
    setClimateItems((items) => items.map((it) => {
      if (it.id !== id) return it;
      const next = { ...it, [field]: field === "watts" ? (parseInt(value) || 0) : value };
      // Changing the type can move a row between the heating and cooling sides
      // of the 8-200(3) comparison, and re-labels it unless the tech has typed
      // their own name over the default.
      if (field === "type") {
        next.side = climateTypeSide(value);
        if (!it.label || it.label === climateTypeLabel(it.type)) next.label = climateTypeLabel(value);
      }
      return next;
    }));
  const removeClimateItem = (id) => setClimateItems((items) => items.filter((it) => it.id !== id));
  const addClimateItem = (type) => setClimateItems((items) => [...items, {
    id: `climate_${Date.now()}`,
    type, side: climateTypeSide(type), label: climateTypeLabel(type),
    watts: climateTypeSide(type) === "ac" ? 5000 : 10000,
    circuit: "", enabled: true, shed: false, gen: "backup",
  }]);

  // ── Section 3c: Electric Range
  const [rangeEnabled, setRangeEnabled] = useState(seed.rangeEnabled ?? true);
  const [rangeNameplateW, setRangeNameplateW] = useState(seed.rangeNameplateW ?? 12000);
  const [rangeShed, setRangeShed] = useState(seed.rangeShed ?? false);
  const [rangeCircuit, setRangeCircuit] = useState(seed.rangeCircuit ?? "");
  const [rangeGen, setRangeGen] = useState(seed.rangeGen ?? "backup");

  // ── Section 3d: Water Heating / Hot Tubs / Pool Heaters
  const [waterHeatingItems, setWaterHeatingItems] = useState(
    Array.isArray(seed.waterHeatingItems)
      ? seed.waterHeatingItems
      : [{ id: "tankless", label: "Tankless Water Heater", watts: 18000, enabled: false }]
  );

  // ── Section 3e: EV Charging
  const [evEnabled, setEvEnabled] = useState(seed.evEnabled ?? false);
  const [evWatts, setEvWatts] = useState(seed.evWatts ?? 12000);
  const [evPreset, setEvPreset] = useState(seed.evPreset ?? "12000");
  const [evems, setEvems] = useState(seed.evems ?? false);
  const [evCircuit, setEvCircuit] = useState(seed.evCircuit ?? "");
  const [evGen, setEvGen] = useState(seed.evGen ?? "backup");

  // ── Generator sizing panel — which fuel the site has. No default that could
  // be mistaken for a fact: the NG and LP columns differ on every unit except
  // the 14 kW, so the panel asks rather than assuming.
  const [genFuel, setGenFuel] = useState(seed.genFuel ?? "ng");
  const [genSpare, setGenSpare] = useState(seed.genSpare ?? 0);
  const [showGenPanel, setShowGenPanel] = useState(false);

  // ── Section 3f: Additional Loads >1500W
  const [additionalItems, setAdditionalItems] = useState(
    Array.isArray(seed.additionalItems)
      ? seed.additionalItems
      : [{ id: "dryer_1", label: "Clothes Dryer", watts: 5500 }]
  );
  const [showAddMenu, setShowAddMenu] = useState(false);

  // ── Section 4: New Load
  const [newLoadType, setNewLoadType] = useState(seed.newLoadType || "");
  const [customNewLoadWatts, setCustomNewLoadWatts] = useState(seed.customNewLoadWatts ?? "");
  const [newLoadShed, setNewLoadShed] = useState(seed.newLoadShed ?? false);

  // ── UI state
  const [showBreakdown, setShowBreakdown] = useState(false);

  // ── New load watts
  const newLoadWatts = useMemo(() => {
    if (presetNewLoad) return presetNewLoad.watts || 0;
    const preset = NEW_LOAD_PRESETS.find((p) => p.label === newLoadType);
    const enteredWatts = parseInt(customNewLoadWatts) || 0;
    if (!newLoadType) return 0;
    if (enteredWatts > 0) return enteredWatts;
    if (preset) return preset.watts;
    return 0;
  }, [presetNewLoad, newLoadType, customNewLoadWatts]);
  const newLoadLabel = presetNewLoad
    ? (presetNewLoad.label || "")
    : (newLoadType === "custom" ? "Custom" : newLoadType);

  // ── Calculate
  const result = useMemo(() => {
    const above = parseFloat(aboveGradeSqFt) || 0;
    const basement = parseFloat(basementSqFt) || 0;
    if (above <= 0 && basement <= 0) return null;

    return calculateCECDemand({
      aboveGradeSqFt: above,
      basementSqFt: basement,
      climateItems,
      heatAcSimultaneous,
      rangeEnabled,
      rangeNameplateW,
      rangeShed,
      waterHeatingItems,
      evEnabled,
      evWatts,
      evems,
      additionalItems,
      newLoadWatts,
      newLoadShed,
      newLoadLabel,
    });
  }, [
    aboveGradeSqFt, basementSqFt,
    climateItems, heatAcSimultaneous,
    rangeEnabled, rangeNameplateW, rangeShed,
    waterHeatingItems,
    evEnabled, evWatts, evems,
    additionalItems,
    newLoadWatts, newLoadShed, newLoadLabel,
  ]);

  // ── Report result to parent
  useEffect(() => {
    if (!onResult || !result) return;
    const serviceAmps = parseInt(serviceSize) || 200;
    onResult({
      demandWatts: result.totalDemand,
      demandAmps: result.demandAmps,
      serviceAmps,
      headroom: Math.round((serviceAmps - result.demandAmps) * 10) / 10,
      overCapacity: result.demandAmps > serviceAmps,
    });
  }, [result, serviceSize, onResult]);

  // ── Report full state snapshot to parent (Save / PDF flow)
  useEffect(() => {
    if (!onStateChange) return;
    const inputs = {
      aboveGradeSqFt, basementSqFt, serviceSize,
      climateItems, heatAcSimultaneous,
      rangeEnabled, rangeNameplateW, rangeShed, rangeCircuit, rangeGen,
      waterHeatingItems,
      evEnabled, evWatts, evPreset, evems, evCircuit, evGen,
      additionalItems,
      newLoadType, customNewLoadWatts, newLoadShed,
      genFuel,
    };
    const serviceAmps = parseInt(serviceSize) || 200;
    onStateChange({ inputs, result, serviceAmps });
  }, [
    onStateChange, result, serviceSize,
    aboveGradeSqFt, basementSqFt,
    climateItems, heatAcSimultaneous,
    rangeEnabled, rangeNameplateW, rangeShed, rangeCircuit, rangeGen,
    waterHeatingItems,
    evEnabled, evWatts, evPreset, evems, evCircuit, evGen,
    additionalItems,
    newLoadType, customNewLoadWatts, newLoadShed,
    genFuel,
  ]);

  // ── Helpers
  const serviceAmps = parseInt(serviceSize) || 200;
  const headroom = result ? Math.round((serviceAmps - result.demandAmps) * 10) / 10 : 0;
  const overCapacity = result ? result.demandAmps > serviceAmps : false;
  const pct = result ? Math.min((result.demandAmps / serviceAmps) * 100, 120) : 0;
  const color = pct > 100 ? "red" : pct > 80 ? "amber" : "green";

  const fmt = (w) => {
    if (w >= 1000) return `${(w / 1000).toFixed(1)}kW`;
    return `${w}W`;
  };

  // ── Water heating helpers
  const addWaterItem = (label, watts) => {
    const id = `water_${Date.now()}`;
    setWaterHeatingItems((prev) => [...prev, { id, label, watts, enabled: true }]);
  };
  const removeWaterItem = (id) => {
    setWaterHeatingItems((prev) => prev.filter((i) => i.id !== id));
  };
  const updateWaterItem = (id, field, value) => {
    setWaterHeatingItems((prev) =>
      prev.map((i) => (i.id === id ? { ...i, [field]: field === "watts" ? (parseInt(value) || 0) : value } : i))
    );
  };
  const toggleWaterItem = (id) => {
    setWaterHeatingItems((prev) =>
      prev.map((i) => (i.id === id ? { ...i, enabled: !i.enabled } : i))
    );
  };

  // ── Additional load helpers
  const addAdditionalItem = (label, watts) => {
    const id = `add_${Date.now()}`;
    setAdditionalItems((prev) => [...prev, { id, label, watts }]);
    setShowAddMenu(false);
  };
  const removeAdditionalItem = (id) => {
    setAdditionalItems((prev) => prev.filter((i) => i.id !== id));
  };
  const updateAdditionalItem = (id, field, value) => {
    setAdditionalItems((prev) =>
      prev.map((i) => (i.id === id ? { ...i, [field]: field === "watts" ? (parseInt(value) || 0) : value } : i))
    );
  };

  const selectNewLoad = (type) => {
    if (newLoadType === type) {
      setNewLoadType("");
      setCustomNewLoadWatts("");
      return;
    }

    setNewLoadType(type);

    if (type === "custom") {
      setCustomNewLoadWatts("");
      return;
    }

    const preset = NEW_LOAD_PRESETS.find((item) => item.label === type);
    setCustomNewLoadWatts(preset ? String(preset.watts) : "");
  };

  // Range calculated preview
  const rangeCalcPreview = useMemo(() => {
    if (!rangeEnabled) return null;
    const np = rangeNameplateW || 12000;
    if (np <= 12000) return 6000;
    return Math.round(6000 + 0.40 * (np - 12000));
  }, [rangeEnabled, rangeNameplateW]);

  // Climate previews come straight off the engine so the card can never show
  // a different number from the total at the bottom of the page.
  const heatRows = climateItems.filter((it) => it.enabled !== false && climateTypeSide(it.type) === "heat");
  const acRows   = climateItems.filter((it) => it.enabled !== false && climateTypeSide(it.type) === "ac");
  const heatConnected = heatRows.filter((it) => !it.shed).reduce((s2, it) => s2 + (Number(it.watts) || 0), 0);
  const acConnected   = acRows.filter((it) => !it.shed).reduce((s2, it) => s2 + (Number(it.watts) || 0), 0);
  const heatingDemandPreview = heatConnected <= 10000
    ? heatConnected
    : Math.round(10000 + 0.75 * (heatConnected - 10000));

  const additionalSectionTotal = additionalItems.reduce((sum, item) => sum + (item.shed ? 0 : (item.watts || 0)), 0);
  const additionalDemandPreview = additionalDemandFor(additionalSectionTotal, rangeEnabled).additionalDemand;
  const additionalRuleHint = rangeEnabled
    ? "Range present: each additional load is counted at 25%."
    : "No range present: first 6,000W is counted at 100%, remainder at 25%.";
  const newLoadPreset = NEW_LOAD_PRESETS.find((p) => p.label === newLoadType);

  // ── Generator sizing ───────────────────────────────────────────────────────
  // Runs the SAME engine over a generator VIEW of these inputs (see
  // projectForGenerator), so the backed-up demand can never drift from the
  // service demand shown above. Only computed while the panel is open — it is
  // a what-if, not part of the CEC calculation.
  const genInputs = useMemo(() => ({
    aboveGradeSqFt: parseFloat(aboveGradeSqFt) || 0,
    basementSqFt: parseFloat(basementSqFt) || 0,
    climateItems, heatAcSimultaneous,
    rangeEnabled, rangeNameplateW, rangeShed, rangeCircuit, rangeGen,
    waterHeatingItems,
    evEnabled, evWatts, evems, evCircuit, evGen,
    additionalItems,
  }), [
    aboveGradeSqFt, basementSqFt, climateItems, heatAcSimultaneous,
    rangeEnabled, rangeNameplateW, rangeShed, rangeCircuit, rangeGen,
    waterHeatingItems, evEnabled, evWatts, evems, evCircuit, evGen, additionalItems,
  ]);

  const genScenario = useMemo(
    () => (showGenPanel && result ? generatorScenario(genInputs, { fuel: genFuel, spareCapacityPct: genSpare }) : null),
    [showGenPanel, result, genInputs, genFuel, genSpare]
  );

  // Every load, with the setter that moves it between backup / shed / off.
  // The fixed loads (range, EV) keep their own state; the list rows are patched
  // in place by id.
  const genLoadRows = useMemo(() => {
    const rows = [];
    for (const it of climateItems) {
      if (it.enabled === false || !(Number(it.watts) > 0)) continue;
      rows.push({ id: it.id, label: it.label, watts: it.watts, circuit: it.circuit,
        mode: it.gen || "backup", set: (m) => updateClimateItem(it.id, "gen", m) });
    }
    if (rangeEnabled) {
      rows.push({ id: "range", label: "Electric range", watts: rangeNameplateW, circuit: rangeCircuit,
        mode: rangeGen, set: setRangeGen });
    }
    for (const it of waterHeatingItems) {
      if (!it.enabled || !(Number(it.watts) > 0)) continue;
      rows.push({ id: it.id, label: it.label, watts: it.watts, circuit: it.circuit,
        mode: it.gen || "backup", set: (m) => updateWaterItem(it.id, "gen", m) });
    }
    if (evEnabled && Number(evWatts) > 0) {
      rows.push({ id: "ev", label: "EV charger", watts: evWatts, circuit: evCircuit,
        mode: evGen, set: setEvGen });
    }
    for (const it of additionalItems) {
      if (!(Number(it.watts) > 0)) continue;
      rows.push({ id: it.id, label: it.label, watts: it.watts, circuit: it.circuit,
        mode: it.gen || "backup", set: (m) => updateAdditionalItem(it.id, "gen", m) });
    }
    return rows;
  }, [climateItems, rangeEnabled, rangeNameplateW, rangeCircuit, rangeGen,
      waterHeatingItems, evEnabled, evWatts, evCircuit, evGen, additionalItems]);

  return (
    <>
      <style>{CSS}</style>
      <div className={`lc-wrap ${embedded ? "lc-embedded" : "lc-standalone"}`}>

        {/* ════════════════════════════════════════════════════════════════════
            SECTION 1: LIVING AREA — Rule 8-110 / 8-200(1)(a)(i)-(ii)
        ════════════════════════════════════════════════════════════════════ */}
        <div className="lc-card">
          <div className="lc-card-title">
            Living Area
            <span className="lc-rule-ref">Rule 8-110</span>
          </div>

          <div className="lc-input-row">
            <span className="lc-input-label">Above-grade</span>
            <input
              className="lc-input"
              type="number"
              inputMode="numeric"
              placeholder="0"
              value={aboveGradeSqFt}
              onChange={(e) => setAboveGradeSqFt(e.target.value)}
            />
            <span className="lc-input-unit">sq ft</span>
          </div>
          <div className="lc-input-hint" style={{ marginBottom: 10, paddingLeft: 102 }}>
            Ground floor + upper floors (100%)
          </div>

          <div className="lc-input-row">
            <span className="lc-input-label">Basement</span>
            <input
              className="lc-input"
              type="number"
              inputMode="numeric"
              placeholder="0"
              value={basementSqFt}
              onChange={(e) => setBasementSqFt(e.target.value)}
            />
            <span className="lc-input-unit">sq ft</span>
          </div>
          <div className="lc-input-hint" style={{ paddingLeft: 102 }}>
            Counted at 75% — below-grade area exceeding 1.8m height
          </div>

          {result && (
            <div className="lc-calc-preview" style={{ marginTop: 10 }}>
              {result.aboveM2.toFixed(1)}m² above + {result.basementM2Adjusted.toFixed(1)}m² basement (75% of {result.basementM2Raw.toFixed(1)}m²) = {result.totalM2.toFixed(1)}m² → Base load: {result.baseLoad.toLocaleString()}W
            </div>
          )}
        </div>

        {/* ════════════════════════════════════════════════════════════════════
            SECTION 2: SERVICE SIZE
        ════════════════════════════════════════════════════════════════════ */}
        <div className="lc-card">
          <div className="lc-card-title">Service Size</div>
          <div className="lc-service-row">
            {SERVICE_SIZES.map((s) => (
              <button
                key={s}
                type="button"
                className={`lc-service-pill ${serviceSize === s ? "active" : ""}`}
                onClick={() => setServiceSize(s)}
              >
                {s}A
              </button>
            ))}
          </div>
        </div>

        {/* ════════════════════════════════════════════════════════════════════
            SECTION 3a/3b: CLIMATE LOADS — Rule 8-200(1)(a)(iii) + 8-200(3)

            One labelled row per appliance. These used to be two bulked boxes,
            so a heat pump, a backup furnace and a garage heater all went in as
            a single number and the breakdown lived only in the tech's head —
            which meant every load-shedding and generator-sizing conversation
            started with "what's inside that number?". The CEC demand factor
            still applies to the SUM of the heating rows exactly as it did to
            the bulked figure: splitting them changes nothing about the
            arithmetic, only about what we know afterwards.
        ════════════════════════════════════════════════════════════════════ */}
        <div className="lc-card">
          <div className="lc-card-title">
            Heating &amp; Cooling Loads
            <span className="lc-rule-ref">8-200(1)(a)(iii)</span>
          </div>

          {climateItems.length === 0 && (
            <div className="lc-input-hint" style={{ marginBottom: 10 }}>
              No heating or cooling loads yet. Add each appliance separately — heat pump,
              furnace, baseboard — and name it, so shedding decisions later know what is what.
            </div>
          )}

          {climateItems.map((item) => {
            const side = climateTypeSide(item.type);
            return (
              <div key={item.id} className="lc-add-item" style={{ flexWrap: "wrap", gap: 6 }}>
                <select
                  className="lc-name-input"
                  style={{ flex: "0 0 auto", minWidth: 132, maxWidth: 152 }}
                  value={item.type}
                  onChange={(e) => updateClimateItem(item.id, "type", e.target.value)}
                  title="What kind of load this is — decides which side of the heating/cooling comparison it lands on"
                >
                  <optgroup label="Heating">
                    {CLIMATE_TYPES.filter((t) => t.side === "heat").map((t) => (
                      <option key={t.key} value={t.key}>{t.label}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Cooling">
                    {CLIMATE_TYPES.filter((t) => t.side === "ac").map((t) => (
                      <option key={t.key} value={t.key}>{t.label}</option>
                    ))}
                  </optgroup>
                </select>
                <input
                  className="lc-name-input"
                  style={{ minWidth: 104 }}
                  value={item.label}
                  placeholder="Name / location"
                  onChange={(e) => updateClimateItem(item.id, "label", e.target.value)}
                  title="Your own name for it — e.g. 'Heat pump — main floor'"
                />
                <input
                  className="lc-watts-input"
                  type="number"
                  inputMode="numeric"
                  value={item.watts}
                  onChange={(e) => updateClimateItem(item.id, "watts", e.target.value)}
                  style={{ opacity: item.shed ? 0.5 : 1 }}
                />
                <span className="lc-watts-unit">W</span>
                <input
                  className="lc-name-input"
                  style={{ flex: "0 0 auto", width: 72 }}
                  value={item.circuit || ""}
                  placeholder="Ckt"
                  onChange={(e) => updateClimateItem(item.id, "circuit", e.target.value)}
                  title="Circuit designation — e.g. 12-14. Optional, and carried into the load-shedding schedule."
                />
                <span className="lc-demand-badge">
                  {item.shed ? "EMS shed → 0W" : side === "ac" ? "→ 100%" : "→ in the heating sum"}
                </span>
                <button
                  type="button"
                  className={`lc-shed-btn ${item.shed ? "on" : ""}`}
                  onClick={() => updateClimateItem(item.id, "shed", !item.shed)}
                  title="EMS-controlled — shed from the SERVICE calc. Generator shedding is a separate question, set in the generator panel below."
                >
                  EMS
                </button>
                <button
                  type="button"
                  className="lc-remove-btn"
                  onClick={() => removeClimateItem(item.id)}
                  title="Remove"
                >
                  ×
                </button>
              </div>
            );
          })}

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
            <button type="button" className="lc-add-btn" onClick={() => addClimateItem("heat_pump")}>+ Heat pump</button>
            <button type="button" className="lc-add-btn" onClick={() => addClimateItem("furnace")}>+ Furnace</button>
            <button type="button" className="lc-add-btn" onClick={() => addClimateItem("baseboard")}>+ Baseboard</button>
            <button type="button" className="lc-add-btn" onClick={() => addClimateItem("central_ac")}>+ A/C</button>
          </div>

          {(heatConnected > 0 || acConnected > 0) && (
            <div className="lc-calc-preview" style={{ marginTop: 10 }}>
              {heatConnected > 0 && (
                <div>
                  Heating: {heatRows.filter((r) => !r.shed).length} load
                  {heatRows.filter((r) => !r.shed).length === 1 ? "" : "s"} ={" "}
                  {heatConnected.toLocaleString()}W connected → {heatingDemandPreview.toLocaleString()}W demand
                  {heatConnected > 10000 ? " — 100% of the first 10,000W + 75% of the rest, applied to the TOTAL" : ""}
                </div>
              )}
              {acConnected > 0 && <div>Cooling: {acConnected.toLocaleString()}W → 100%</div>}
            </div>
          )}

          {/* Only a real question while both sides are counted — a shed system
              has already dropped out of the heat/AC comparison. */}
          {heatConnected > 0 && acConnected > 0 && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#c4c9d4", marginBottom: 6 }}>
                Can heating and A/C run at the same time?
              </div>
              <div className="lc-service-row">
                <button
                  type="button"
                  className={`lc-service-pill ${!heatAcSimultaneous ? "active" : ""}`}
                  onClick={() => setHeatAcSimultaneous(false)}
                  style={{ fontSize: 12, padding: "6px 12px" }}
                >
                  No — use larger
                </button>
                <button
                  type="button"
                  className={`lc-service-pill ${heatAcSimultaneous ? "active" : ""}`}
                  onClick={() => setHeatAcSimultaneous(true)}
                  style={{ fontSize: 12, padding: "6px 12px" }}
                >
                  Yes — add both
                </button>
              </div>
              <div className="lc-input-hint" style={{ marginTop: 6 }}>
                {heatAcSimultaneous
                  ? "Independent systems (e.g. baseboard heat + central A/C). Both nameplate demands are added."
                  : "Shared air handler, heat pump, or interlocked systems. Per Rule 8-200(3), only the larger is counted."}
              </div>
              <div className={`lc-note ${heatAcSimultaneous ? "warn" : ""}`} style={{ marginTop: 8 }}>
                <span className="lc-note-icon">⚡</span>
                <span>
                  {heatAcSimultaneous ? (
                    <>
                      <strong>Adding both:</strong>{" "}
                      heating ({heatingDemandPreview.toLocaleString()}W) + A/C ({acConnected.toLocaleString()}W) ={" "}
                      {(heatingDemandPreview + acConnected).toLocaleString()}W
                    </>
                  ) : (
                    <>
                      <strong>Rule 8-200(3):</strong>{" "}
                      {heatingDemandPreview >= acConnected
                        ? `Using heating (${heatingDemandPreview.toLocaleString()}W ≥ ${acConnected.toLocaleString()}W A/C)`
                        : `Using A/C (${acConnected.toLocaleString()}W > ${heatingDemandPreview.toLocaleString()}W heating)`}
                    </>
                  )}
                </span>
              </div>
            </div>
          )}

          <div className="lc-note" style={{ marginTop: 8 }}>
            <span className="lc-note-icon">ℹ</span>
            <span>Baseboard heat with thermostatic room controls: enter total connected load.</span>
          </div>
        </div>


        {/* ════════════════════════════════════════════════════════════════════
            SECTION 3c: ELECTRIC RANGE — Rule 8-200(1)(a)(iv)
        ════════════════════════════════════════════════════════════════════ */}
        <div className="lc-card">
          <div className="lc-card-title">
            Electric Range
            <span className="lc-rule-ref">8-200(1)(a)(iv)</span>
          </div>
          <Toggle
            on={rangeEnabled}
            onToggle={() => setRangeEnabled(!rangeEnabled)}
            label="Electric Range"
            subLabel="6,000W for ≤12kW; 6,000 + 40% of excess above 12kW"
          >
            {rangeEnabled && (
              <>
                <input
                  className="lc-watts-input"
                  type="number"
                  inputMode="numeric"
                  value={rangeNameplateW}
                  onChange={(e) => setRangeNameplateW(parseInt(e.target.value) || 0)}
                  style={{ opacity: rangeShed ? 0.5 : 1 }}
                />
                <span className="lc-watts-unit">W nameplate</span>
                <input
                  className="lc-name-input"
                  style={{ flex: "0 0 auto", width: 72 }}
                  value={rangeCircuit}
                  placeholder="Ckt"
                  onChange={(e) => setRangeCircuit(e.target.value)}
                  title="Circuit designation — e.g. 12-14. Optional, and carried into the load-shedding schedule."
                />
                <button
                  type="button"
                  className={`lc-shed-btn ${rangeShed ? "on" : ""}`}
                  onClick={() => setRangeShed(!rangeShed)}
                  title="EMS-controlled — fully shed from the calc"
                >
                  EMS
                </button>
              </>
            )}
          </Toggle>
          {rangeEnabled && rangeCalcPreview !== null && (
            <div className="lc-calc-preview">
              {rangeShed
                ? `${(rangeNameplateW / 1000).toFixed(1)}kW range → EMS shed, 0W demand (still counts as "range present" for additional loads)`
                : `${(rangeNameplateW / 1000).toFixed(1)}kW range → ${rangeCalcPreview.toLocaleString()}W demand`}
            </div>
          )}
        </div>

        {/* ════════════════════════════════════════════════════════════════════
            SECTION 3d: WATER HEATING / HOT TUBS / POOL HEATERS — Rule 8-200(1)(a)(v)
        ════════════════════════════════════════════════════════════════════ */}
        <div className="lc-card">
          <div className="lc-card-title">
            Water Heating, Hot Tubs, Pool/Spa Heaters
            <span className="lc-rule-ref">8-200(1)(a)(v)</span>
          </div>
          <div className="lc-input-hint" style={{ marginBottom: 8 }}>
            Use this section for tankless water heaters, hot tubs, pool or spa heaters, and similar loads at 100% of nameplate.
          </div>
          <div className="lc-input-hint" style={{ marginBottom: 8 }}>
            Standard storage tank water heaters belong under Additional Loads so Rule 8-200(1)(a)(vii) is applied correctly.
          </div>

          {waterHeatingItems.map((item) => (
            <div key={item.id} className="lc-sub-item">
              <button
                type="button"
                className={`lc-toggle ${item.enabled ? "on" : "off"}`}
                onClick={() => toggleWaterItem(item.id)}
                style={{ width: 32, height: 20, minWidth: 32 }}
              >
                <span className="lc-toggle-knob" style={{ width: 14, height: 14, top: 3 }} />
              </button>
              <input
                className="lc-name-input"
                value={item.label}
                onChange={(e) => updateWaterItem(item.id, "label", e.target.value)}
                style={{ flex: 1, opacity: item.enabled ? 1 : 0.5 }}
              />
              <input
                className="lc-watts-input"
                type="number"
                inputMode="numeric"
                value={item.watts}
                onChange={(e) => updateWaterItem(item.id, "watts", e.target.value)}
                disabled={!item.enabled}
                style={{ opacity: item.enabled && !item.shed ? 1 : 0.5 }}
              />
              <span className="lc-watts-unit">W</span>
              <input
                className="lc-name-input"
                style={{ flex: "0 0 auto", width: 72 }}
                value={item.circuit || ""}
                placeholder="Ckt"
                onChange={(e) => updateWaterItem(item.id, "circuit", e.target.value)}
                title="Circuit designation — e.g. 12-14. Optional, and carried into the load-shedding schedule."
              />
              <button
                type="button"
                className={`lc-shed-btn ${item.shed ? "on" : ""}`}
                onClick={() => updateWaterItem(item.id, "shed", !item.shed)}
                disabled={!item.enabled}
                title="EMS-controlled — fully shed from the calc"
              >
                EMS
              </button>
              <button
                type="button"
                className="lc-remove-btn"
                onClick={() => removeWaterItem(item.id)}
                title="Remove"
              >
                ×
              </button>
            </div>
          ))}

          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
            <button
              type="button"
              className="lc-add-btn"
              onClick={() => addWaterItem("Tankless Water Heater", 18000)}
            >
              + Tankless (18,000W)
            </button>
            <button
              type="button"
              className="lc-add-btn"
              onClick={() => addWaterItem("Steam Shower / Steamer", 12000)}
            >
              + Steamer (12,000W)
            </button>
            <button
              type="button"
              className="lc-add-btn"
              onClick={() => addWaterItem("Hot Tub / Spa", 6000)}
            >
              + Hot Tub (6,000W)
            </button>
            <button
              type="button"
              className="lc-add-btn"
              onClick={() => addWaterItem("Pool Heater", 5000)}
            >
              + Pool Heater (5,000W)
            </button>
          </div>

          {waterHeatingItems.length > 0 && (
            <div className="lc-section-total">
              Section total: {waterHeatingItems.filter((i) => i.enabled && !i.shed).reduce((s, i) => s + (i.watts || 0), 0).toLocaleString()}W @ 100%
              {waterHeatingItems.some((i) => i.enabled && i.shed) && (
                <span className="lc-shed-note"> · EMS-shed loads excluded</span>
              )}
            </div>
          )}
        </div>

        {/* ════════════════════════════════════════════════════════════════════
            SECTION 3e: EV CHARGING — Rule 8-200(1)(a)(vi)
        ════════════════════════════════════════════════════════════════════ */}
        <div className="lc-card">
          <div className="lc-card-title">
            Electric Vehicle Charging
            <span className="lc-rule-ref">8-200(1)(a)(vi)</span>
          </div>
          <Toggle
            on={evEnabled}
            onToggle={() => setEvEnabled(!evEnabled)}
            label="EV Charger"
            subLabel="100% of nameplate"
          />
          {evEnabled && (
            <>
              <div className="lc-ev-pills" style={{ marginTop: 8 }}>
                {EV_PRESETS.map((p) => (
                  <button
                    key={p.watts}
                    type="button"
                    className={`lc-ev-pill ${evPreset === String(p.watts) ? "active" : ""}`}
                    onClick={() => {
                      setEvPreset(String(p.watts));
                      setEvWatts(p.watts);
                    }}
                  >
                    {p.label}
                  </button>
                ))}
                <button
                  type="button"
                  className={`lc-ev-pill ${evPreset === "custom" ? "active" : ""}`}
                  onClick={() => setEvPreset("custom")}
                >
                  Custom
                </button>
              </div>
              {evPreset === "custom" && (
                <div className="lc-input-row">
                  <span className="lc-input-label">Watts</span>
                  <input
                    className="lc-input"
                    type="number"
                    inputMode="numeric"
                    value={evWatts}
                    onChange={(e) => setEvWatts(parseInt(e.target.value) || 0)}
                  />
                  <span className="lc-input-unit">W</span>
                </div>
              )}
              <div className="lc-input-row">
                <span className="lc-input-label">Circuit</span>
                <input
                  className="lc-input"
                  value={evCircuit}
                  placeholder="e.g. 40-42"
                  onChange={(e) => setEvCircuit(e.target.value)}
                />
              </div>
              <div className="lc-checkbox-row">
                <input
                  type="checkbox"
                  className="lc-checkbox"
                  checked={evems}
                  onChange={(e) => setEvems(e.target.checked)}
                  id="evems-check"
                />
                <label htmlFor="evems-check">
                  EVEMS (Energy Management System) — sheds the whole charger
                </label>
              </div>
              {evems && (
                <div className="lc-calc-preview" style={{ marginTop: 6 }}>
                  {evWatts.toLocaleString()}W charger fully shed by EVEMS = 0W demand
                </div>
              )}
            </>
          )}
        </div>

        {/* ════════════════════════════════════════════════════════════════════
            SECTION 3f: ADDITIONAL LOADS >1500W — Rule 8-200(1)(a)(vii)
        ════════════════════════════════════════════════════════════════════ */}
        <div className="lc-card">
          <div className="lc-card-title">
            Additional Loads Over 1,500W
            <span className="lc-rule-ref">8-200(1)(a)(vii)</span>
          </div>
          <div className="lc-input-hint" style={{ marginBottom: 8 }}>
            Everything else not covered above, including storage tank water heaters and dryers.
          </div>
          <div className="lc-input-hint" style={{ marginBottom: 8 }}>
            {additionalRuleHint}
          </div>

          {additionalItems.map((item) => (
            <div key={item.id} className="lc-add-item">
              <input
                className="lc-name-input"
                value={item.label}
                onChange={(e) => updateAdditionalItem(item.id, "label", e.target.value)}
              />
              <input
                className="lc-watts-input"
                type="number"
                inputMode="numeric"
                value={item.watts}
                onChange={(e) => updateAdditionalItem(item.id, "watts", e.target.value)}
                style={{ opacity: item.shed ? 0.5 : 1 }}
              />
              <span className="lc-watts-unit">W</span>
              <span className="lc-demand-badge">
                {item.shed
                  ? "EMS shed -> 0W"
                  : rangeEnabled
                  ? `-> ${Math.round((item.watts || 0) * 0.25).toLocaleString()}W`
                  : "Counted in no-range demand total"}
              </span>
              <input
                className="lc-name-input"
                style={{ flex: "0 0 auto", width: 72 }}
                value={item.circuit || ""}
                placeholder="Ckt"
                onChange={(e) => updateAdditionalItem(item.id, "circuit", e.target.value)}
                title="Circuit designation — e.g. 12-14. Optional, and carried into the load-shedding schedule."
              />
              <button
                type="button"
                className={`lc-shed-btn ${item.shed ? "on" : ""}`}
                onClick={() => updateAdditionalItem(item.id, "shed", !item.shed)}
                title="EMS-controlled — fully shed from the calc"
              >
                EMS
              </button>
              <button
                type="button"
                className="lc-remove-btn"
                onClick={() => removeAdditionalItem(item.id)}
                title="Remove"
              >
                ×
              </button>
            </div>
          ))}

          <div className="lc-add-dropdown">
            <button
              type="button"
              className="lc-add-btn"
              onClick={() => setShowAddMenu(!showAddMenu)}
            >
              + Add Load
            </button>
            {showAddMenu && (
              <div className="lc-add-menu">
                {ADDITIONAL_LOAD_PRESETS.map((p) => (
                  <button
                    key={p.label}
                    type="button"
                    className="lc-add-menu-item"
                    onClick={() => addAdditionalItem(p.label, p.defaultW)}
                  >
                    {p.label} ({p.defaultW.toLocaleString()}W)
                  </button>
                ))}
                <button
                  type="button"
                  className="lc-add-menu-item"
                  onClick={() => addAdditionalItem("Custom Load", 2000)}
                >
                  Custom…
                </button>
              </div>
            )}
          </div>

          {additionalItems.length > 0 && (
            <div className="lc-section-total">
              {additionalSectionTotal.toLocaleString()}W nameplate -> {additionalDemandPreview.toLocaleString()}W demand
            </div>
          )}
        </div>

        {/* ════════════════════════════════════════════════════════════════════
            SECTION 4: NEW LOAD TO ADD
        ════════════════════════════════════════════════════════════════════ */}
        {!presetNewLoad && (
          <div className="lc-card">
            <div className="lc-new-load">
              <div className="lc-new-load-title">
                New Load to Add (Optional)
              </div>
              <div className="lc-input-hint" style={{ marginBottom: 10 }}>
                Check if a new load fits on the existing panel
              </div>
              <div className="lc-input-hint" style={{ marginBottom: 10 }}>
                Preset values are starting points. Enter the actual nameplate watts for the equipment on site.
              </div>
              <div className="lc-nl-pills">
                {NEW_LOAD_PRESETS.filter((p) => p.watts > 0).map((p) => (
                  <button
                    key={p.label}
                    type="button"
                    className={`lc-nl-pill ${newLoadType === p.label ? "active" : ""}`}
                    onClick={() => selectNewLoad(p.label)}
                  >
                    {p.label}
                  </button>
                ))}
                <button
                  type="button"
                  className={`lc-nl-pill ${newLoadType === "custom" ? "active" : ""}`}
                  onClick={() => selectNewLoad("custom")}
                >
                  Custom
                </button>
              </div>
              {newLoadType && (
                <div className="lc-nl-custom">
                  <input
                    className="lc-input"
                    type="number"
                    inputMode="numeric"
                    placeholder={newLoadPreset ? `${newLoadPreset.watts}` : "Watts"}
                    value={customNewLoadWatts}
                    onChange={(e) => setCustomNewLoadWatts(e.target.value)}
                    style={{ opacity: newLoadShed ? 0.5 : 1 }}
                  />
                  <span className="lc-input-unit">W</span>
                  <button
                    type="button"
                    className={`lc-shed-btn ${newLoadShed ? "on" : ""}`}
                    onClick={() => setNewLoadShed(!newLoadShed)}
                    title="EMS-controlled — fully shed from the calc"
                  >
                    EMS
                  </button>
                </div>
              )}
              {newLoadType && newLoadShed && newLoadWatts > 0 && (
                <div className="lc-calc-preview" style={{ marginTop: 6 }}>
                  {newLoadWatts.toLocaleString()}W new load → EMS shed, 0W demand
                </div>
              )}
            </div>
          </div>
        )}

        {presetNewLoad && presetNewLoad.watts > 0 && (
          <div className="lc-card">
            <div className="lc-new-load">
              <div className="lc-new-load-title">New Load</div>
              <div style={{ fontSize: 13, color: "#e8eaf0", fontWeight: 600 }}>
                {presetNewLoad.label || "New Load"} — {presetNewLoad.watts.toLocaleString()}W
              </div>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            SECTION 5: RESULTS
        ════════════════════════════════════════════════════════════════════ */}
        {result && (
          <div className={`lc-result ${color}`}>
            <div className="lc-result-title">Calculated Demand</div>

            {/* Capacity bar */}
            <div className="lc-capacity-bar-wrap">
              <div className="lc-capacity-labels">
                <span>0A</span>
                <span>{serviceAmps}A service</span>
              </div>
              <div className="lc-capacity-bar">
                <div
                  className={`lc-capacity-fill ${color}`}
                  style={{ width: `${Math.min(pct, 100)}%` }}
                />
              </div>
            </div>

            {/* Summary */}
            <div className={`lc-summary ${color}`}>
              <span className="lc-summary-icon">
                {color === "green" ? "✅" : color === "amber" ? "⚠️" : "🔴"}
              </span>
              <span className="lc-summary-text">
                {result.demandAmps}A demand on {serviceAmps}A service
                {overCapacity
                  ? ` — OVER CAPACITY by ${Math.abs(headroom)}A`
                  : ` — ${headroom}A headroom`}
              </span>
            </div>
            {result.shedLoads.length > 0 && (
              <div className="lc-shed-note" style={{ fontSize: 11, margin: "-4px 0 8px" }}>
                EMS sheds {result.shedLoads.map((s) => s.label).join(", ")} ({result.shedWattsTotal.toLocaleString()}W nameplate, counted at 0W)
              </div>
            )}

            {/* Toggle breakdown */}
            <button
              type="button"
              onClick={() => setShowBreakdown(!showBreakdown)}
              style={{
                background: "none",
                border: "none",
                color: "#a5ce3a",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                padding: "4px 0",
                fontFamily: "'IBM Plex Sans', sans-serif",
              }}
            >
              {showBreakdown ? "▼ Hide" : "▶ Show"} Full Breakdown
            </button>

            {showBreakdown && (
              <div className="lc-breakdown">
                {/* Base load */}
                <div className="lc-br-row">
                  <span className="lc-br-label">Base load ({result.totalM2.toFixed(1)}m²)</span>
                  <span className="lc-br-value">{result.baseLoad.toLocaleString()}W</span>
                </div>
                <div className="lc-br-row lc-br-sub">
                  <span className="lc-br-label">Rule 8-200(1)(a)(i)-(ii): 5,000W first 90m² + 1,000W/90m²</span>
                  <span className="lc-br-value"></span>
                </div>

                {/* Heating or AC */}
                {result.heatingOrAC > 0 && result.heatAcMode === "both" && (
                  <>
                    <div className="lc-br-row">
                      <span className="lc-br-label">Electric heating (demand factored)</span>
                      <span className="lc-br-value">{Math.round(result.heatingDemand).toLocaleString()}W</span>
                    </div>
                    <div className="lc-br-row lc-br-sub">
                      <span className="lc-br-label">
                        Rule 8-200(1)(a)(iii): {result.heatingWatts.toLocaleString()}W → 100% first 10kW + 75% remainder
                      </span>
                      <span className="lc-br-value"></span>
                    </div>
                    <div className="lc-br-row">
                      <span className="lc-br-label">Air conditioning</span>
                      <span className="lc-br-value">{result.acDemand.toLocaleString()}W</span>
                    </div>
                    <div className="lc-br-row lc-br-sub">
                      <span className="lc-br-label">
                        Rule 8-200(3): heat &amp; A/C can run simultaneously — both added at 100%
                      </span>
                      <span className="lc-br-value"></span>
                    </div>
                  </>
                )}
                {result.heatingOrAC > 0 && result.heatAcMode !== "both" && (
                  <>
                    <div className="lc-br-row">
                      <span className="lc-br-label">
                        {result.usingHeating
                          ? `Electric heating (demand factored)`
                          : `Air conditioning`}
                      </span>
                      <span className="lc-br-value">
                        {result.heatingOrAC.toLocaleString()}W
                      </span>
                    </div>
                    {result.usingHeating && result.heatingWatts > 0 && (
                      <div className="lc-br-row lc-br-sub">
                        <span className="lc-br-label">
                          Rule 8-200(1)(a)(iii): {result.heatingWatts.toLocaleString()}W → 100% first 10kW + 75% remainder
                        </span>
                        <span className="lc-br-value"></span>
                      </div>
                    )}
                    {!result.usingHeating && (
                      <div className="lc-br-row lc-br-sub">
                        <span className="lc-br-label">
                          Rule 8-200(1)(a)(iii): AC at 100% nameplate{result.heatingDemand > 0 ? " (larger than heating)" : ""}
                        </span>
                        <span className="lc-br-value"></span>
                      </div>
                    )}
                  </>
                )}

                {/* Range */}
                {result.rangeEnabled && !result.rangeShed && (
                  <>
                    <div className="lc-br-row">
                      <span className="lc-br-label">Electric range</span>
                      <span className="lc-br-value">{result.rangeCalculated.toLocaleString()}W</span>
                    </div>
                    <div className="lc-br-row lc-br-sub">
                      <span className="lc-br-label">
                        Rule 8-200(1)(a)(iv): {(result.rangeNameplate / 1000).toFixed(1)}kW nameplate → {result.rangeNameplate <= 12000 ? "6,000W" : `6,000 + 40% of ${((result.rangeNameplate - 12000) / 1000).toFixed(1)}kW excess`}
                      </span>
                      <span className="lc-br-value"></span>
                    </div>
                  </>
                )}

                {/* Water heating */}
                {result.waterHeatingTotal > 0 && (
                  <>
                    <div className="lc-br-row">
                      <span className="lc-br-label">Water heating / hot tubs / pool</span>
                      <span className="lc-br-value">{result.waterHeatingTotal.toLocaleString()}W</span>
                    </div>
                    <div className="lc-br-row lc-br-sub">
                      <span className="lc-br-label">Rule 8-200(1)(a)(v): 100% nameplate</span>
                      <span className="lc-br-value"></span>
                    </div>
                  </>
                )}

                {/* EV */}
                {evEnabled && evWatts > 0 && (
                  <>
                    <div className="lc-br-row">
                      <span className="lc-br-label">EV charging{evems ? " (EVEMS — shed)" : ""}</span>
                      <span className="lc-br-value">{result.evDemand.toLocaleString()}W</span>
                    </div>
                    <div className="lc-br-row lc-br-sub">
                      <span className="lc-br-label">
                        {evems
                          ? `Rule 8-200(1)(a)(vi): ${evWatts.toLocaleString()}W nameplate fully shed by EVEMS`
                          : "Rule 8-200(1)(a)(vi): 100% nameplate"}
                      </span>
                      <span className="lc-br-value"></span>
                    </div>
                  </>
                )}

                {/* Additional loads */}
                {result.additionalDemand > 0 && (
                  <>
                    <div className="lc-br-row">
                      <span className="lc-br-label">Additional loads (&gt;1,500W)</span>
                      <span className="lc-br-value">{result.additionalDemand.toLocaleString()}W</span>
                    </div>
                    <div className="lc-br-row lc-br-sub">
                      <span className="lc-br-label">
                        {result.additionalRuleBranch === "with_range"
                          ? `Rule 8-200(1)(a)(vii): range present, ${result.additionalRawTotal.toLocaleString()}W x 25%`
                          : `Rule 8-200(1)(a)(vii): no range, first ${result.additionalFirstBlock.toLocaleString()}W at 100% + ${result.additionalRemainder.toLocaleString()}W x 25%`}
                      </span>
                      <span className="lc-br-value"></span>
                    </div>
                  </>
                )}

                {/* New load */}
                {result.newLoadDemand > 0 && (
                  <div className="lc-br-row">
                    <span className="lc-br-label">
                      New load ({presetNewLoad ? presetNewLoad.label : newLoadType || "Custom"})
                    </span>
                    <span className="lc-br-value">{result.newLoadDemand.toLocaleString()}W</span>
                  </div>
                )}

                {/* EMS-shed loads — nameplate listed, counted at 0W */}
                {result.shedLoads.length > 0 && (
                  <>
                    <div className="lc-br-row">
                      <span className="lc-br-label" style={{ color: "#f59e0b" }}>
                        EMS-shed loads ({result.shedWattsTotal.toLocaleString()}W nameplate)
                      </span>
                      <span className="lc-br-value" style={{ color: "#f59e0b" }}>0W</span>
                    </div>
                    {result.shedLoads.map((s) => (
                      <div key={s.key} className="lc-br-row lc-br-sub">
                        <span className="lc-br-label">{s.label}: {(s.watts || 0).toLocaleString()}W</span>
                        <span className="lc-br-value"></span>
                      </div>
                    ))}
                  </>
                )}

                {/* Totals */}
                <div className="lc-br-row total">
                  <span className="lc-br-label">Total calculated demand</span>
                  <span className="lc-br-value">{result.totalDemand.toLocaleString()}W</span>
                </div>
                <div className="lc-br-row total">
                  <span className="lc-br-label">Calculated amps (÷ 240V)</span>
                  <span className="lc-br-value">{result.demandAmps}A</span>
                </div>
                <div className="lc-br-row total">
                  <span className="lc-br-label">Service capacity</span>
                  <span className="lc-br-value">{serviceAmps}A</span>
                </div>
                <div className="lc-br-row total">
                  <span className="lc-br-label">
                    {overCapacity ? "Over capacity by" : "Headroom"}
                  </span>
                  <span
                    className="lc-br-value"
                    style={{ color: overCapacity ? "#f87171" : "#34d399" }}
                  >
                    {overCapacity ? `-${Math.abs(headroom)}A` : `${headroom}A`}
                  </span>
                </div>
              </div>
            )}

            <div className="lc-footer">
              Based on CEC 2024 (26th Edition) Rule 8-200
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════
            GENERATOR SIZING — a what-if on top of the calc, not part of it.

            The CEC "EMS" flags above answer "does this count against the
            SERVICE?". This panel answers a different question: "what does the
            GENERATOR have to carry?" — and they are genuinely different. A hot
            tub counts in full against the service and is the first thing an
            SMM drops on the genny. Sizing reads the published Generac Next
            Generation ratings for the FUEL selected; every unit except the
            14 kW makes less power on natural gas.
        ════════════════════════════════════════════════════════════════════ */}
        {result && (
          <div className="lc-card" style={{ marginTop: 14 }}>
            <div className="lc-card-title" style={{ cursor: "pointer" }} onClick={() => setShowGenPanel(!showGenPanel)}>
              {showGenPanel ? "▼" : "▶"} Generator Sizing &amp; Load Shedding
              <span className="lc-rule-ref">Generac Next Gen</span>
            </div>

            {!showGenPanel && (
              <div className="lc-input-hint">
                Open to test what the generator carries, shed loads onto SMMs, and size a unit
                against the real natural-gas / propane ratings.
              </div>
            )}

            {showGenPanel && (
              <>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: "#c4c9d4" }}>Fuel:</span>
                  {FUELS.map((f) => (
                    <button
                      key={f.key}
                      type="button"
                      className={`lc-service-pill ${genFuel === f.key ? "active" : ""}`}
                      style={{ fontSize: 12, padding: "6px 12px" }}
                      onClick={() => setGenFuel(f.key)}
                    >
                      {f.label}
                    </button>
                  ))}
                  <span style={{ fontSize: 12, fontWeight: 600, color: "#c4c9d4", marginLeft: 10 }}>Spare capacity:</span>
                  <input
                    className="lc-watts-input"
                    type="number"
                    inputMode="numeric"
                    style={{ width: 62 }}
                    value={genSpare}
                    onChange={(e) => setGenSpare(Math.max(0, parseInt(e.target.value) || 0))}
                    title="Optional headroom on top of the calculated demand. CEC demand is already diversified, so 0 is the honest default."
                  />
                  <span className="lc-watts-unit">%</span>
                </div>

                {genLoadRows.map((row) => (
                  <div key={row.id} className="lc-add-item" style={{ flexWrap: "wrap", gap: 6 }}>
                    <span style={{ flex: 1, minWidth: 140, fontSize: 13, color: "#e6e9ef" }}>
                      {row.label}
                      {row.circuit ? <span style={{ color: "#8b93a7" }}> · ckt {row.circuit}</span> : null}
                    </span>
                    <span className="lc-demand-badge">
                      {(Number(row.watts) || 0).toLocaleString()}W · {Math.round(((Number(row.watts) || 0) / 240) * 10) / 10}A
                    </span>
                    <div style={{ display: "flex", gap: 4 }}>
                      {GEN_MODES.map((m) => (
                        <button
                          key={m.key}
                          type="button"
                          className={`lc-service-pill ${row.mode === m.key ? "active" : ""}`}
                          style={{ fontSize: 11, padding: "4px 9px" }}
                          onClick={() => row.set(m.key)}
                          title={m.hint}
                        >
                          {m.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}

                {genScenario && (
                  <div style={{ marginTop: 12 }}>
                    <div className="lc-calc-preview">
                      <div>
                        Service demand: {genScenario.service.demand_watts.toLocaleString()}W ·{" "}
                        {genScenario.service.demand_amps}A · {genScenario.service.demand_kw} kW
                      </div>
                      <div style={{ fontWeight: 600, color: "#e6e9ef" }}>
                        On the generator: {genScenario.generator.demand_watts.toLocaleString()}W ·{" "}
                        {genScenario.generator.demand_amps}A · {genScenario.generator.demand_kw} kW
                      </div>
                      {genScenario.saved_watts > 0 && (
                        <div>
                          Shedding / excluding saves {genScenario.saved_watts.toLocaleString()}W ({genScenario.saved_amps}A)
                        </div>
                      )}
                    </div>

                    {genScenario.smms.length > 0 && (
                      <div className="lc-note" style={{ marginTop: 8 }}>
                        <span className="lc-note-icon">⚙</span>
                        <span>
                          {genScenario.smms.length} load-shedding module
                          {genScenario.smms.length === 1 ? "" : "s"}:{" "}
                          {genScenario.smms.map((m) => `${m.label} (${m.amps}A → ${m.module.replace("SMM_", "")})`).join(", ")}
                        </span>
                      </div>
                    )}

                    {genScenario.sizing.error && (
                      <div className="lc-note warn" style={{ marginTop: 8 }}>
                        <span className="lc-note-icon">⚠</span><span>{genScenario.sizing.error}</span>
                      </div>
                    )}

                    {genScenario.sizing.over_lineup && (
                      <div className="lc-note warn" style={{ marginTop: 8 }}>
                        <span className="lc-note-icon">⚠</span>
                        <span>
                          {genScenario.generator.demand_amps}A is past the top of the air-cooled lineup — the largest
                          ({genScenario.sizing.largest.label}) makes {genScenario.sizing.largest.amps}A on{" "}
                          {genFuel === "ng" ? "natural gas" : "propane"}. Shed more load or move to liquid-cooled.
                        </span>
                      </div>
                    )}

                    {genScenario.sizing.pick && (
                      <div className={`lc-result ${genScenario.sizing.pick.headroom_a >= 0 ? "green" : "amber"}`} style={{ marginTop: 10 }}>
                        <div className="lc-result-title">
                          Generac {genScenario.sizing.pick.label} — model {genScenario.sizing.pick.sku}
                        </div>
                        <div style={{ fontSize: 13, color: "#c4c9d4", lineHeight: 1.7 }}>
                          <div>
                            {genScenario.sizing.pick.kw} kW on {genFuel === "ng" ? "natural gas" : "propane"} ={" "}
                            {genScenario.sizing.pick.amps}A · {genScenario.sizing.pick.mlcb_a}A main breaker
                          </div>
                          <div>Headroom over the backed-up load: {genScenario.sizing.pick.headroom_a}A</div>
                          {genScenario.sizing.pick.caution && (
                            <div style={{ color: "#f59e0b" }}>⚠ {genScenario.sizing.pick.caution}</div>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: "#8b93a7", marginTop: 8 }}>
                          {genScenario.sizing.smaller && (
                            <div>
                              Next down: {genScenario.sizing.smaller.label} ({genScenario.sizing.smaller.sku}) —{" "}
                              {genScenario.sizing.smaller.amps}A, {genScenario.sizing.smaller.headroom_a}A headroom
                            </div>
                          )}
                          {genScenario.sizing.larger && (
                            <div>
                              Next up: {genScenario.sizing.larger.label} ({genScenario.sizing.larger.sku}) —{" "}
                              {genScenario.sizing.larger.amps}A, {genScenario.sizing.larger.headroom_a}A headroom
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {!result && (
          <div className="lc-note">
            <span className="lc-note-icon">📐</span>
            <span>Enter the living area above to calculate demand.</span>
          </div>
        )}
      </div>
    </>
  );
}


